import React, { useState, useEffect, type JSX, ReactNode, useMemo, ReactElement, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-hot-toast';
import { DropdownToggle, DropdownMenu, DropdownItem, Container, UncontrolledButtonDropdown, Spinner } from 'reactstrap';
import { Table, Card } from 'antd';
import styled from '@emotion/styled';
import { FaEllipsisV } from 'react-icons/fa';
import differenceInHours from 'date-fns/differenceInHours';
import { UncontrolledButtonDropdownProps } from 'reactstrap/types/lib/ButtonDropdown';
import { ColumnType } from 'antd/es/table';

type DataSourceItem = {
  [key: string]: any;
};

const getMultiDataSource = (selectedRowKeys: React.Key[], dataSource: DataSourceItem[], rowIdentificator: string) => {
  return dataSource?.filter((item) => selectedRowKeys.includes(item[rowIdentificator]));
};

const TableViewConatiner = styled.div`
  .ant-checkbox-disabled .ant-checkbox-inner {
    background-color: #afafaf;
  }
`;

export const DivRow = styled.div`
  display: flex;
  flex-direction: row;

  .btn-group {
    margin: 10px;
    a {
      color: white;
      text-decoration: none;
    }
  }
  span {
    align-self: center;
  }
`;

const TableColumnDivider = styled.div<{
  $resizable: boolean;
  $isResizing: boolean;
}>`
  position: absolute;
  top: 0;
  right: -4px;
  width: 8px;
  height: 100%;
  z-index: 3;
  cursor: ${({ $resizable }) => ($resizable ? 'col-resize' : 'default')};

  &::after {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    width: ${({ $isResizing }) => ($isResizing ? '2px' : '1px')};
    height: 70%;
    background: ${({ $isResizing }) => ($isResizing ? 'var(--productPrimaryDark)' : 'rgba(255, 255, 255, 0.7)')};
    transform: translate(-50%, -50%);
    transition:
      width 120ms ease,
      background 120ms ease;
  }

  ${({ $resizable, $isResizing }) =>
    $resizable &&
    !$isResizing &&
    `
      &:hover::after {
        width: 2px;
        background: var(--productPrimaryDark);
      }
    `}
`;

const StyledHeaderCell = styled.th`
  position: relative;

  &::before {
    display: none !important;
  }
`;

const TruncatedText = ({ children }: { children: React.ReactNode }) => {
  const originalTitlesRef = useRef<Array<[HTMLElement, string]>>([]);

  const handleMouseEnter = (event: React.MouseEvent<HTMLSpanElement>) => {
    const element = event.currentTarget;

    const isTruncated = element.scrollWidth > element.clientWidth;

    if (!isTruncated) {
      return;
    }

    // Use the actually rendered/formatted value
    element.title = element.innerText.trim();

    // Temporarily disable nested tooltips, e.g. Datestamp ISO timestamp
    const elementsWithTitle = element.querySelectorAll<HTMLElement>('[title]');

    originalTitlesRef.current = Array.from(elementsWithTitle).map((child) => [child, child.title]);

    elementsWithTitle.forEach((child) => {
      child.removeAttribute('title');
    });
  };

  const handleMouseLeave = (event: React.MouseEvent<HTMLSpanElement>) => {
    event.currentTarget.removeAttribute('title');

    // Restore original tooltips
    originalTitlesRef.current.forEach(([element, title]) => {
      element.title = title;
    });

    originalTitlesRef.current = [];
  };

  return (
    <span className="vl-table-column-truncate" onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
      {children}
    </span>
  );
};

export const DataCell = ({ item, rowData }) => {
  const { t } = useTranslation('common');

  const orderHoursInOverdue = 72;
  const isLabsOrderOverdue =
    rowData.lab_test_status_code === 'ORDER_TBC' &&
    differenceInHours(new Date(), rowData.appt_completed_date) >= orderHoursInOverdue;

  const display =
    rowData.cr_survey_overdue ||
    rowData.sr_appt_overdue ||
    rowData.sr_appt2_overdue ||
    rowData.cr_overdue ||
    isLabsOrderOverdue
      ? 'red'
      : 'black';

  // Only to render the overdue column in cases
  if (item.dataSourceKey === 'cr_survey_overdue') {
    const value =
      rowData.cr_survey_overdue || rowData.sr_appt_overdue || rowData.sr_appt2_overdue || rowData.cr_overdue;
    return <span>{value ? t('Yes') : t('No')}</span>;
  }

  if (item.dataSourceKey === 'has_img_reqs') {
    return <span style={{ color: display }}>{rowData.has_img_reqs ? t('Yes') : t('No')}</span>;
  }

  if (item.columnDataFormat) {
    const content = <item.columnDataFormat data={rowData[item.dataSourceKey]} entireData={rowData} />;

    return (
      <span style={{ color: display }}>
        {item.truncate === false ? content : <TruncatedText>{content}</TruncatedText>}
      </span>
    );
  }

  const content = rowData[item.dataSourceKey];

  return item.truncate === false ? <span>{content}</span> : <TruncatedText>{content}</TruncatedText>;
};

export const ActionDropdown = ({
  actionItems,
  rowData,
  customDataTestID,
  ...rest
}: {
  actionItems: ActionItem[];
  rowData: DataSourceItem;
  customDataTestID?: string;
} & UncontrolledButtonDropdownProps) => {
  const { t } = useTranslation('common');
  const notMultiActions = actionItems.filter((item) => !item.isMultiAction);
  return (
    <UncontrolledButtonDropdown data-testid={customDataTestID || 'action-dropdown'} direction="start" {...rest}>
      <div>
        <DropdownToggle tag="a" data-testid="action-dropdown-toggle" className="action-dropdown-toggle">
          <FaEllipsisV style={{ color: 'var(--productPrimaryDark)' }} />
        </DropdownToggle>
      </div>
      <DropdownMenu>
        {notMultiActions?.map((menu, mi) => {
          if (menu.hidden) return null;
          if (menu.divider) {
            return <DropdownItem key={`${menu.label}-${mi}`} divider />;
          } else if (!menu.isVisible || menu.isVisible(rowData)) {
            const formattedLabel = menu.labelFormat?.(rowData);
            const actionDropdownLabel = React.isValidElement<{ children?: React.ReactNode }>(formattedLabel)
              ? formattedLabel.props.children
              : menu.label;
            return (
              <DropdownItem
                data-testid={actionDropdownLabel}
                key={`${menu.label}-${mi}`}
                onClick={(e) => {
                  if (!menu.onClick) return;
                  e.stopPropagation();
                  menu.onClick(rowData);
                }}
                disabled={menu.isDisabled?.(rowData)}
              >
                {menu.labelFormat ? menu.labelFormat(rowData) : menu.label}
              </DropdownItem>
            );
          } else {
            return null;
          }
        })}
        {!notMultiActions && <DropdownItem>{t('Missing actionItems')}</DropdownItem>}
      </DropdownMenu>
    </UncontrolledButtonDropdown>
  );
};

export const MultiActionDropdown = ({
  multiActionItems,
  multiDataSource,
  selectedRowKeys,
  disabled
}: {
  multiActionItems: ActionItem[];
  multiDataSource: DataSourceItem[];
  selectedRowKeys: React.Key[];
  disabled: boolean;
}) => {
  const { t } = useTranslation('common');
  return (
    <UncontrolledButtonDropdown data-testid="action-dropdown" direction="end">
      <DropdownToggle tag="a">
        <div className="ant-space-item">
          <button
            className="vl-button btn btn-secondary"
            disabled={!selectedRowKeys?.length}
            data-testid="actions-dropdown"
          >
            {t('Actions')}
          </button>
        </div>
      </DropdownToggle>
      <DropdownMenu>
        {multiActionItems?.map((menu, mi) => {
          if (menu.divider) {
            return <DropdownItem key={`${menu.label}-${mi}`} divider />;
          }

          if (!menu.isVisible) {
            return (
              <DropdownItem
                key={`${menu.label}-${mi}`}
                onClick={(e) => {
                  e.stopPropagation();
                  menu.onMultiActionClick?.(selectedRowKeys);
                }}
                disabled={disabled || menu.isDisabled?.(multiDataSource)}
              >
                {menu.label}
              </DropdownItem>
            );
          }

          return null;
        })}
        {!multiActionItems && <DropdownItem>{t('Missing actionItems')}</DropdownItem>}
      </DropdownMenu>
    </UncontrolledButtonDropdown>
  );
};

export type ActionItem = {
  label?: string;
  onClick?: (data: unknown) => {} | void;
  isMultiAction?: boolean;
  onMultiActionClick?: (params: unknown) => void;
  getCheckboxDisabledProp?: (record: unknown) => boolean;
  isVisible?: (param: any) => boolean;
  divider?: boolean;
  isDisabled?: (param: { actionable: number }) => boolean;
  labelFormat?: (param: any) => ReactElement<any> | string | JSX.Element;
  hidden?: boolean;
  getActionItemsFromData?: (data: unknown) => ActionItem[];
};

export type PinnedPosition = 'left' | 'right';

export type ColumnResizeSize = 'small' | 'medium' | 'large';

export const COLUMN_WIDTH_LIMITS = {
  small: {
    min: 80,
    max: 140
  },
  medium: {
    min: 90,
    max: 280
  },
  large: {
    min: 140,
    max: 320
  }
} as const;

export type HeaderItemType = {
  display: JSX.Element | ReactNode | string; // header title
  dataSourceKey?: string; // map to dataSource key
  customizationKey?: string; // Unique key for column customization when dataSourceKey is shared by multiple columns.
  // Keeps complex column content in the expandable "+" row instead of rendering it in the main table.
  expandableOnly?: boolean;
  resizable?: boolean;
  resizeSize?: ColumnResizeSize;
  width?: ColumnType<DataSourceItem>['width'];
  minWidth?: number;
  maxWidth?: number;
  truncate?: boolean;
  hidden?: boolean;
  mandatory?: boolean;
  pinned?: PinnedPosition;
  columnDataFormat?:
    | string
    | (({ data, entireData }: { data: any; entireData: any }) => any)
    | ((data: { [key: string]: any }) => JSX.Element); // if present column data will be format. columnDataFormat: ({ data }) => {return data}
  isAction?: boolean; // if true, the column data changed to menu
  actionItems?: ActionItem[]; // action menu items
  getActionItemsFromData?: (data: unknown) => ActionItem[];
  isSortable?: boolean;
  filterMultiple?: boolean;
  filterKey?: string;
  filters?: { text: string | ReactNode; value: string | number | bigint | boolean }[];
  responsive?: ('xs' | 'sm' | 'md' | 'lg' | 'xl' | 'xxl')[];
  filterable?: any; // Not sure should update
  dateFilter?: boolean;
  defaultFilteredValue?: (string | number | bigint | boolean)[];
  style?: React.CSSProperties;
  onClick?: (e: any) => {};
  onHeaderCell?: ColumnType<any>['onHeaderCell'];
};

type ResizableHeaderCellProps = React.ThHTMLAttributes<HTMLTableCellElement> & {
  columnKey?: string;
  resizable?: boolean;
  showDivider?: boolean;
  minWidth?: number;
  maxWidth?: number;

  onResizeStart?: (widths: Record<string, number>) => void;
  onResize?: (key: string, width: number) => void;
  onResizeEnd?: (widths: Record<string, number>) => void;
};

const ResizableHeaderCell = ({
  columnKey,
  resizable,
  showDivider,
  minWidth = 100,
  maxWidth = 280,
  onResizeStart,
  onResize,
  onResizeEnd,
  children,
  ...rest
}: ResizableHeaderCellProps) => {
  const cellRef = useRef<HTMLTableCellElement>(null);
  const [isResizing, setIsResizing] = useState(false);

  const handlePointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
    if (event.button !== 0 || !columnKey || !cellRef.current) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();

    const cell = cellRef.current;
    const nextCell = cell.nextElementSibling as HTMLTableCellElement | null;

    const nextKey = nextCell?.dataset.columnKey;

    if (!nextCell || !nextKey) {
      return;
    }

    const startWidth = cell.offsetWidth;
    const nextStartWidth = nextCell.offsetWidth;

    const nextMinWidth = Number(nextCell.dataset.minWidth ?? 0);

    const nextMaxWidth = Number(nextCell.dataset.maxWidth ?? Infinity);

    const startX = event.clientX;

    const renderedWidth = cell.getBoundingClientRect().width;

    const scale = renderedWidth / cell.offsetWidth;

    const headerRow = cell.closest('tr');

    const headerCells = Array.from(headerRow?.querySelectorAll<HTMLTableCellElement>('th[data-column-key]') ?? []);

    const initialWidths = Object.fromEntries(
      headerCells.flatMap((headerCell) => {
        const key = headerCell.dataset.columnKey;

        return key ? [[key, headerCell.offsetWidth]] : [];
      })
    );

    let hasMoved = false;
    let finalWidth = startWidth;
    let finalNextWidth = nextStartWidth;

    const handlePointerMove = (moveEvent: PointerEvent) => {
      if ((moveEvent.buttons & 1) === 0) {
        finishResize();
        return;
      }

      const screenDelta = moveEvent.clientX - startX;

      if (!hasMoved && Math.abs(screenDelta) < 3) {
        return;
      }

      if (!hasMoved) {
        hasMoved = true;

        setIsResizing(true);

        document.body.classList.add('vl-table-column-resizing');

        onResizeStart?.(initialWidths);
      }

      const rawDelta = screenDelta / scale;

      const minDelta = Math.max(minWidth - startWidth, nextStartWidth - nextMaxWidth);

      const maxDelta = Math.min(maxWidth - startWidth, nextStartWidth - nextMinWidth);

      const delta = Math.min(Math.max(rawDelta, minDelta), maxDelta);

      finalWidth = startWidth + delta;
      finalNextWidth = nextStartWidth - delta;

      onResize?.(columnKey, finalWidth);
      onResize?.(nextKey, finalNextWidth);
    };

    const finishResize = () => {
      if (hasMoved) {
        onResizeEnd?.({
          ...initialWidths,
          [columnKey]: finalWidth,
          [nextKey]: finalNextWidth
        });
      }

      setIsResizing(false);

      document.body.classList.remove('vl-table-column-resizing');

      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('pointerup', finishResize);
      window.removeEventListener('pointercancel', finishResize);
      window.removeEventListener('blur', finishResize);
    };

    window.addEventListener('pointermove', handlePointerMove);
    window.addEventListener('pointerup', finishResize);
    window.addEventListener('pointercancel', finishResize);
    window.addEventListener('blur', finishResize);
  };

  return (
    <StyledHeaderCell
      ref={cellRef}
      data-column-key={columnKey}
      data-min-width={minWidth}
      data-max-width={maxWidth}
      data-resizable={resizable}
      {...rest}
    >
      {children}

      {columnKey && showDivider && (
        <TableColumnDivider
          $resizable={Boolean(resizable)}
          $isResizing={isResizing}
          onPointerDown={resizable ? handlePointerDown : undefined}
          onClick={(event) => {
            event.preventDefault();
            event.stopPropagation();
          }}
          onDoubleClick={(event) => {
            event.preventDefault();
            event.stopPropagation();
          }}
        />
      )}
    </StyledHeaderCell>
  );
};

const TableDataView = ({
  busy = false,
  noDataFoundText = 'No Data Found',
  headerItems = [],
  dataSource = [],
  onPaginationNumberClick = (pageNumber: number) => {},
  onPaginationPreviousClick = () => {},
  onPaginationNextClick = () => {},
  resetPagination = false,
  paginationNumberOfScreens = 5,
  noPadding = false,
  handleTableChange = (pagination: any, ...rest) => {},
  hideColumnList = [],
  keyValue = undefined,
  expandedRowRender = null,
  columnTransformer = undefined,
  clearSelectedRowKeys = false,
  shouldRequireSearchFilters,
  onColumnResize,
  ...extraProps
}: {
  busy?: boolean;
  noDataFoundText?: string;
  headerItems: HeaderItemType[];
  dataSource: any[];
  onPaginationNumberClick?: (pageNumber: number) => void;
  onPaginationPreviousClick?: () => void;
  onPaginationNextClick?: () => void;
  resetPagination?: boolean;
  paginationNumberOfScreens?: number;
  noPadding?: boolean;
  handleTableChange?: (pagination: any, ...rest: any[]) => void;
  hideColumnList?: any[];
  keyValue?: any;
  expandedRowRender?: any;
  columnTransformer?: any;
  clearSelectedRowKeys?: boolean;
  shouldRequireSearchFilters?: boolean;
  [key: string]: any;
  onColumnResize?: (widths: Record<string, number>) => void;
}) => {
  const { 'data-testid': dataTestId, ...otherProps } = extraProps;
  const { t } = useTranslation('common');
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [liveWidths, setLiveWidths] = useState<Record<string, number>>({});

  const handleColumnResizeStart = (widths: Record<string, number>) => {
    setLiveWidths(widths);
  };

  const handleColumnResize = (key: string, width: number) => {
    setLiveWidths((current) => ({
      ...current,
      [key]: width
    }));
  };

  const handleColumnResizeEnd = (widths: Record<string, number>) => {
    onColumnResize?.(widths);
  };

  useEffect(() => {
    setSelectedRowKeys([]);
  }, [clearSelectedRowKeys]);

  const rowIdentificator = keyValue || otherProps.rowKey;

  // --- Inner Components
  const expandableHeaderItems = headerItems.filter((item) => item.expandableOnly);
  const visibleHeaderItems = headerItems.filter((item) => !item.expandableOnly);
  const transformedHeaders = visibleHeaderItems
    .map(({ responsive: _, ...item }) => ({
      ...item,
      filterResetToDefaultFilteredValue: !!item?.defaultFilteredValue,
      dataIndex: item.dataSourceKey,
      key: item.customizationKey ?? item.dataSourceKey,
      title: (
        <span className="vl-table-column-header-text" data-testid={item.dataSourceKey}>
          {item.display}
        </span>
      )
    }))
    .map((item, index, items) => {
      const key = item.customizationKey ?? item.dataSourceKey;

      const isLastColumn = index === items.length - 1;

      const ACTION_COLUMN_MIN_WIDTH = 85;

      const resizeSize = item.isAction ? 'small' : (item.resizeSize ?? 'medium');

      const resizeLimits = COLUMN_WIDTH_LIMITS[resizeSize];

      const minWidth = item.isAction
        ? Math.max(item.minWidth ?? 0, ACTION_COLUMN_MIN_WIDTH)
        : (item.minWidth ?? resizeLimits.min);

      const maxWidth = item.maxWidth ?? resizeLimits.max;

      const isResizable = Boolean(onColumnResize) && item.resizable !== false;

      const originalHeaderCellProps = item.onHeaderCell?.(item) ?? {};

      const hasHeaderControls = item.isSortable || item.filterable || item.filters || item.dateFilter;

      const effectiveMinWidth = minWidth + (hasHeaderControls ? 20 : 0);

      const width = (key ? liveWidths[key] : undefined) ?? item.width ?? effectiveMinWidth;

      return {
        ...item,
        width,

        sorter: item.isSortable,
        align: item.isAction ? 'center' : undefined,

        onHeaderCell: () => ({
          ...originalHeaderCellProps,
          columnKey: key,
          resizable: isResizable,
          showDivider: !isLastColumn,
          minWidth: effectiveMinWidth,
          maxWidth,
          onResizeStart: handleColumnResizeStart,
          onResize: handleColumnResize,
          onResizeEnd: handleColumnResizeEnd
        }),
        render: (text, rowData) => {
          if (columnTransformer) {
            return columnTransformer(item, rowData);
          }
          if (item.isAction) {
            const actionItems = item.actionItems || item.getActionItemsFromData?.(rowData);
            return <ActionDropdown actionItems={actionItems} rowData={rowData} />;
          }
          return <DataCell item={item} rowData={rowData} />;
        }
      };
    });
  const expandableItems = [
    ...(hideColumnList ?? []),
    ...expandableHeaderItems.filter((expandableItem) => {
      const expandableKey = expandableItem.customizationKey ?? expandableItem.dataSourceKey;

      return !hideColumnList?.some((hiddenItem) => {
        const hiddenKey = hiddenItem.customizationKey ?? hiddenItem.dataSourceKey;

        return hiddenKey === expandableKey;
      });
    })
  ];
  const transformedHideHeaders = expandableItems
    ?.map((item) => ({
      ...item,
      dataIndex: item.dataSourceKey,
      key: item.customizationKey ?? item.dataSourceKey,
      title: item.display
    }))
    .map((item) => ({
      ...item,
      sorter: item.isSortable,
      render: (text, rowData) => {
        if (item.isAction) {
          return <ActionDropdown actionItems={item.actionItems} rowData={rowData} />;
        }
        return <DataCell item={item} rowData={rowData} />;
      }
    }));

  const onSelectedRowKeysChange = (newRowKeys: React.Key[]) => {
    if (newRowKeys.length <= 10) {
      setSelectedRowKeys(newRowKeys);
    } else {
      toast.error('Cannot select more than 10 items.');
    }
  };

  const multiSelectActions = headerItems
    .filter((item) => item.isAction)[0]
    ?.actionItems?.filter((item) => item?.isMultiAction);

  const multiDataSource = getMultiDataSource(selectedRowKeys, dataSource, rowIdentificator);
  const isDisabledCheck = multiSelectActions?.length && (multiSelectActions[0]?.getCheckboxDisabledProp as Function);
  const isAnyMultiActionExist = multiSelectActions?.length > 0;

  const rowSelection = isAnyMultiActionExist
    ? {
        selectedRowKeys,
        preserveSelectedRowKeys: true,
        onChange: onSelectedRowKeysChange,
        getCheckboxProps: (record: DataSourceItem) => ({
          disabled: isDisabledCheck(record) || false
        }),
        renderCell: (checked, record, index, originNode) => {
          return originNode;
        },
        hideSelectAll: true
      }
    : undefined;

  const emptyState = useMemo(() => {
    if (busy) {
      return (
        <div style={{ textAlign: 'center', padding: '20px' }}>
          <Spinner size="sm" style={{ marginRight: '8px' }} />
          {t('Loading...')}
        </div>
      );
    }

    if (shouldRequireSearchFilters) {
      return 'Please enter some search criteria to view data in the table';
    }

    return noDataFoundText;
  }, [busy, shouldRequireSearchFilters, noDataFoundText]);

  return (
    <Container fluid={true} css={{ padding: 0 }}>
      {isAnyMultiActionExist && (
        <DivRow>
          <MultiActionDropdown
            multiActionItems={multiSelectActions}
            multiDataSource={multiDataSource}
            disabled={!selectedRowKeys?.length}
            selectedRowKeys={selectedRowKeys}
          />
          {!!selectedRowKeys?.length && (
            <span>
              {t('Selected')} {selectedRowKeys?.length} {t('items')}
            </span>
          )}
        </DivRow>
      )}

      <TableViewConatiner data-testid={dataTestId}>
        <Table
          {...otherProps}
          tableLayout="fixed"
          components={{
            header: {
              cell: ResizableHeaderCell
            }
          }}
          className="table-data-view"
          locale={{
            emptyText: emptyState
          }}
          rowKey={
            otherProps.rowKey ? otherProps.rowKey : (item) => item[keyValue] || `item-${dataSource.indexOf(item)}`
          }
          // sometimes Table component doesn't re-render as we change amount of elements that we want to display, for example for smaller screens
          // we want to show less elements, so we provide headerItems with different amount of elements
          // since we process headerItems with .map it is definately new Array, but sometimes component doesn't re-render.
          // We add key value to help react to re-render this component
          key={transformedHeaders?.length}
          dataSource={dataSource}
          columns={transformedHeaders}
          pagination={false}
          expandable={{
            expandedRowRender:
              expandedRowRender ||
              ((record) => (
                <Card style={{ width: 'auto' }}>
                  {' '}
                  {transformedHideHeaders?.map((item, index) => (
                    <p key={`${item?.title}-${index}`}>
                      <b>{item?.title} :</b>{' '}
                      {item.columnDataFormat ? (
                        <item.columnDataFormat data={record[item.dataIndex]} entireData={record} />
                      ) : (
                        record[item?.dataIndex]
                      )}
                    </p>
                  ))}
                </Card>
              )),
            rowExpandable: () => !!(transformedHideHeaders?.length > 0 || expandedRowRender)
          }}
          onChange={handleTableChange}
          rowSelection={rowSelection}
        />
      </TableViewConatiner>
    </Container>
  );
};

export { TableDataView };
